import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";

type Theme = "light" | "dark";

interface ThemeContextValue {
  theme: Theme;
  canToggle: boolean;
  toggleTheme: () => void;
}

const STORAGE_KEY = "fp_theme";

const ThemeContext = createContext<ThemeContextValue>({
  theme: "light",
  canToggle: false,
  toggleTheme: () => {},
});

function getInitialTheme(enabled: boolean): Theme {
  if (!enabled) return "light";
  if (typeof window === "undefined") return "light";
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored === "dark" || stored === "light") return stored;
  return "light";
}

interface ThemeProviderProps {
  enabled?: boolean;
  children: React.ReactNode;
}

export function ThemeProvider({
  enabled = false,
  children,
}: ThemeProviderProps) {
  const [theme, setTheme] = useState<Theme>(() => getInitialTheme(enabled));

  useEffect(() => {
    if (!enabled) {
      document.documentElement.classList.remove("dark");
      return;
    }
    const root = document.documentElement;
    if (theme === "dark") {
      root.classList.add("dark");
    } else {
      root.classList.remove("dark");
    }
    localStorage.setItem(STORAGE_KEY, theme);
  }, [theme, enabled]);

  const toggleTheme = useCallback(() => {
    if (!enabled) return;
    setTheme((prev) => (prev === "dark" ? "light" : "dark"));
  }, [enabled]);

  return (
    <ThemeContext.Provider
      value={{
        theme: enabled ? theme : "light",
        canToggle: enabled,
        toggleTheme,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}
